/**
 * Sentry Configuration for React Web
 * Error tracking and performance monitoring
 *
 * Setup Instructions:
 * 1. Create account at https://sentry.io (Free tier: 10K errors/month)
 * 2. Create React project
 * 3. Add REACT_APP_SENTRY_DSN to your .env file
 * 4. App will automatically start tracking errors
 *
 * If REACT_APP_SENTRY_DSN is not configured, all functions gracefully skip without errors
 */

import logger from './logger';

// Sentry SDK will be imported dynamically when configured
let Sentry: any = null;
let sentryInitialized = false;

// Get DSN from environment
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';

// Configuration
const config = {
  dsn: SENTRY_DSN,
  environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development',
  enabled: !!SENTRY_DSN && process.env.NODE_ENV === 'production',
  debug: process.env.NODE_ENV === 'development',
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.2 : 1.0, // 20% sampling in production
  replaysSessionSampleRate: 0.1, // 10% of sessions recorded
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors recorded
};

/**
 * Initialize Sentry
 * Call this before rendering the app in index.tsx
 */
export async function initSentry(): Promise<void> {
  if (!config.dsn) {
    logger.info('[Sentry] DSN not configured - error tracking disabled');
    return;
  }

  if (sentryInitialized) {
    return;
  }

  try {
    // Dynamic import to avoid errors when package not installed
    Sentry = await import('@sentry/react');

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      enabled: config.enabled,
      debug: config.debug,

      // Performance Monitoring
      tracesSampleRate: config.tracesSampleRate,

      // Session Replay (requires @sentry/browser)
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

      // Filter sensitive data before sending
      beforeSend(event: any, hint: any) {
        // Don't send in development
        if (process.env.NODE_ENV !== 'production') {
          return null;
        }

        // Remove sensitive data from request
        if (event.request?.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['Authorization'];
          delete event.request.headers['cookie'];
          delete event.request.headers['Cookie'];
        }

        // Filter sensitive query parameters
        if (event.request?.query_string && typeof event.request.query_string === 'string') {
          const sensitiveParams = ['password', 'token', 'secret', 'api_key', 'apiKey'];
          let queryString = event.request.query_string;
          sensitiveParams.forEach(param => {
            if (queryString.includes(param)) {
              queryString = queryString.replace(
                new RegExp(`${param}=[^&]*`, 'gi'),
                `${param}=[REDACTED]`
              );
            }
          });
          event.request.query_string = queryString;
        }

        // Filter URL-based breadcrumbs for auth pages
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.filter((breadcrumb: any) => {
            if (breadcrumb.category === 'navigation') {
              const url = breadcrumb.data?.to || breadcrumb.data?.url || '';
              if (url.includes('login') || url.includes('password') || url.includes('reset')) {
                return false;
              }
            }
            return true;
          });
        }

        return event;
      },

      // Ignore common non-actionable errors
      ignoreErrors: [
        // Network errors
        'Network request failed',
        'NetworkError',
        'Failed to fetch',
        'Load failed',
        'ChunkLoadError',

        // Browser errors
        'ResizeObserver loop',
        'Non-Error promise rejection captured',
        'Script error.',

        // Extension/plugin errors
        'Extension context invalidated',
        'chrome-extension://',
        'moz-extension://',

        // React errors that are not actionable
        "Can't perform a React state update on an unmounted component",

        // Auth errors (expected behavior)
        'JsonWebTokenError',
        'TokenExpiredError',
        'Unauthorized',
      ],

      // Deny specific URLs
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Firefox extensions
        /^moz-extension:\/\//i,
        // Safari extensions
        /^safari-extension:\/\//i,
      ],

      // Release tracking
      release: process.env.REACT_APP_VERSION || '1.0.0',
    });

    sentryInitialized = true;
    logger.info('[Sentry] Initialized successfully');
  } catch (error) {
    // Silently fail if Sentry package not installed
    logger.info('[Sentry] Package not installed - error tracking disabled');
  }
}

/**
 * Capture exception with optional context
 */
export function captureException(
  error: Error | unknown,
  context?: Record<string, any>
): void {
  if (!Sentry || !config.dsn) {
    // Log to console in development
    if (process.env.NODE_ENV !== 'production') {
      logger.error('[Error]', error, context);
    }
    return;
  }

  try {
    Sentry.captureException(error, {
      extra: context,
    });
  } catch {
    // Silently fail
  }
}

/**
 * Capture message with severity level
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, any>
): void {
  if (!Sentry || !config.dsn) {
    if (process.env.NODE_ENV !== 'production') {
      logger.info(`[${level.toUpperCase()}]`, message, context);
    }
    return;
  }

  try {
    Sentry.captureMessage(message, {
      level,
      extra: context,
    });
  } catch {
    // Silently fail
  }
}

/**
 * Set user context for error tracking
 * Call after login to associate errors with user
 */
export function setUserContext(user: {
  id: string;
  email?: string;
  username?: string;
  organizationId?: string | number;
}): void {
  if (!Sentry || !config.dsn) return;

  try {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
      organization: String(user.organizationId || ''),
    });
  } catch {
    // Silently fail
  }
}

/**
 * Clear user context
 * Call on logout
 */
export function clearUserContext(): void {
  if (!Sentry || !config.dsn) return;

  try {
    Sentry.setUser(null);
  } catch {
    // Silently fail
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, any>,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  if (!Sentry || !config.dsn) return;

  try {
    Sentry.addBreadcrumb({
      category,
      message,
      data,
      level,
    });
  } catch {
    // Silently fail
  }
}

/**
 * Set custom tag for filtering in Sentry dashboard
 */
export function setTag(key: string, value: string): void {
  if (!Sentry || !config.dsn) return;

  try {
    Sentry.setTag(key, value);
  } catch {
    // Silently fail
  }
}

/**
 * Wrap component with Sentry error boundary
 * Use for critical components
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ReactNode
): React.ComponentType<P> {
  if (!Sentry) {
    return Component;
  }

  try {
    return Sentry.withErrorBoundary(Component, {
      fallback: fallback || null,
    });
  } catch {
    return Component;
  }
}

/**
 * Get Sentry Error Boundary component
 * Use as wrapper in JSX
 */
export function getErrorBoundary(): React.ComponentType<any> | null {
  if (!Sentry) return null;

  try {
    return Sentry.ErrorBoundary;
  } catch {
    return null;
  }
}

// Export config for reference
export const sentryConfig = config;

export default {
  init: initSentry,
  captureException,
  captureMessage,
  setUserContext,
  clearUserContext,
  addBreadcrumb,
  setTag,
  withErrorBoundary,
  getErrorBoundary,
};
